import { useState } from 'react';
import { BillingToExpenseService } from '@/services/billing-to-expense.service';

export default function BillingToExpenseConversion() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    converted: number;
    skipped: number;
    expenseIds: string[];
    errors: string[];
  } | null>(null);

  const [filters, setFilters] = useState({
    status: '',
    startDate: '',
    endDate: '',
    limit: '',
    employerId: '',
    providerId: '',
    skipExisting: true,
  });

  const handleConvert = async () => {
    setLoading(true);
    setResult(null);

    try {
      const options: {
        status?: number;
        startDate?: Date;
        endDate?: Date;
        limit?: number;
        employerId?: string;
        providerId?: string;
        skipExisting?: boolean;
      } = {
        skipExisting: filters.skipExisting,
      };

      if (filters.status) {
        options.status = parseInt(filters.status);
      }
      if (filters.startDate) {
        options.startDate = new Date(filters.startDate);
      }
      if (filters.endDate) {
        options.endDate = new Date(filters.endDate);
      }
      if (filters.limit) {
        options.limit = parseInt(filters.limit);
      }
      if (filters.employerId) {
        options.employerId = filters.employerId;
      }
      if (filters.providerId) {
        options.providerId = filters.providerId;
      }

      const conversionResult =
        await BillingToExpenseService.convertBillingTransactionsToExpenses(
          options
        );

      setResult(conversionResult);
    } catch (error) {
      setResult({
        success: false,
        converted: 0,
        skipped: 0,
        expenseIds: [],
        errors: [
          error instanceof Error ? error.message : 'Unknown error occurred',
        ],
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-6">
          Convert Billing Transactions to Expenses
        </h1>

        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Filters</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) =>
                  setFilters({ ...filters, status: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">All Statuses</option>
                <option value="0">Pending</option>
                <option value="1">Invoiced</option>
                <option value="2">Paid</option>
                <option value="3">Cancelled</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Limit
              </label>
              <input
                type="number"
                value={filters.limit}
                onChange={(e) =>
                  setFilters({ ...filters, limit: e.target.value })
                }
                placeholder="No limit"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) =>
                  setFilters({ ...filters, startDate: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) =>
                  setFilters({ ...filters, endDate: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Employer ID
              </label>
              <input
                type="text"
                value={filters.employerId}
                onChange={(e) =>
                  setFilters({ ...filters, employerId: e.target.value })
                }
                placeholder="Filter by employer"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Provider ID
              </label>
              <input
                type="text"
                value={filters.providerId}
                onChange={(e) =>
                  setFilters({ ...filters, providerId: e.target.value })
                }
                placeholder="Filter by provider"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={filters.skipExisting}
                onChange={(e) =>
                  setFilters({ ...filters, skipExisting: e.target.checked })
                }
                className="mr-2"
              />
              <span className="text-sm text-gray-700">
                Skip transactions that already have expenses
              </span>
            </label>
          </div>

          <button
            onClick={handleConvert}
            disabled={loading}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? 'Converting...' : 'Convert to Expenses'}
          </button>
        </div>

        {result && (
          <div
            className={`bg-white rounded-lg shadow-md p-6 ${
              result.success ? 'border-l-4 border-green-500' : 'border-l-4 border-red-500'
            }`}
          >
            <h2 className="text-xl font-semibold mb-4">Conversion Results</h2>

            <div className="space-y-2 mb-4">
              <div className="flex justify-between">
                <span className="font-medium">Success:</span>
                <span className={result.success ? 'text-green-600' : 'text-red-600'}>
                  {result.success ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Converted:</span>
                <span>{result.converted}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Skipped:</span>
                <span>{result.skipped}</span>
              </div>
              {result.expenseIds.length > 0 && (
                <div>
                  <span className="font-medium">Expense IDs:</span>
                  <div className="mt-2 max-h-40 overflow-y-auto">
                    {result.expenseIds.map((id) => (
                      <div key={id} className="text-sm text-gray-600 font-mono">
                        {id}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {result.errors.length > 0 && (
              <div className="mt-4">
                <h3 className="font-medium text-red-600 mb-2">Errors:</h3>
                <ul className="list-disc list-inside space-y-1">
                  {result.errors.map((error, index) => (
                    <li key={index} className="text-sm text-red-600">
                      {error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
